// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import MqttWebSocket from '../MqttWebSocket';
import { getConfigValue } from '../config';
import { useAuth } from './AuthContext';

const SharedMqttContext = createContext();

export function useSharedMqtt() {
  const context = useContext(SharedMqttContext);

  // Also check the singleton client directly for more accurate status
  // This helps when the React state hasn't caught up with the actual connection
  if (context && sharedMqttClient) {
    const actualConnected = sharedMqttClient.isConnected?.() ?? false;
    if (actualConnected && !context.isConnected) {
      // The client is actually connected but state says otherwise
      // This can happen due to timing issues - log it for debugging
    }
    // Return the actual connection status if it's more positive than state
    return {
      ...context,
      isConnected: context.isConnected || actualConnected,
    };
  }

  return context;
}

// Singleton instance of MQTT client
let sharedMqttClient = null;

// IMPROVED: Use Map with unique IDs instead of Set with function references
// This prevents the "orphaned listener" problem where function reference changes cause stale listeners
let sharedMqttListeners = new Map(); // Map<listenerId, { callback, addedAt, lastCalled }>
let listenerIdCounter = 0;
let listenerCallCount = 0; // Track total calls for debugging
let lastMessageTime = Date.now(); // Track when last message was received at singleton level

// Batched message dispatch: queue incoming MQTT messages and flush them to
// listeners on a short timer so the WebSocket `onmessage` handler returns
// almost immediately.  This prevents Chrome "[Violation] 'message' handler
// took Nms" warnings when the main thread is contended (e.g. during
// login re-render cascade).
let messageQueue = [];
let dispatchTimer = null;

const dispatchQueuedMessages = () => {
  dispatchTimer = null;
  const queue = messageQueue;
  if (queue.length === 0) return;
  messageQueue = [];

  const listenerCount = sharedMqttListeners.size;
  if (listenerCount === 0) return;

  for (const msg of queue) {
    for (const [id, listenerInfo] of sharedMqttListeners.entries()) {
      try {
        if (typeof listenerInfo.callback === 'function') {
          listenerInfo.callback(msg);
          listenerInfo.lastCalled = Date.now();
        } else {
          sharedMqttListeners.delete(id);
        }
      } catch (err) {
        // Don't remove erroring listeners immediately, they might recover
      }
    }
  }
  listenerCallCount += queue.length;
};

// Helper function to generate a unique client ID for the shared connection
const generateSharedClientId = () => {
  // Always generate a fresh, unique client ID to prevent conflicts
  // This prevents issues with multiple tabs, refreshes, or stale sessions
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  const browserRandom = Math.random().toString(36).substring(2, 4);

  const clientId = `dashboard_shared_${timestamp}_${random}_${browserRandom}`;

  return clientId;
};

// Clean up stale listeners that haven't been called in a while
// This prevents memory leaks from orphaned listeners
// IMPORTANT: Listeners marked as permanent (e.g., MqttDebugContext) are NEVER removed.
const cleanupStaleListeners = () => {
  const now = Date.now();
  const STALE_THRESHOLD = 10 * 60 * 1000; // 10 minutes without being called
  let removedCount = 0;

  for (const [id, listener] of sharedMqttListeners.entries()) {
    // Never remove permanent listeners (core context providers that live for the app lifetime)
    if (listener.permanent) continue;

    const timeSinceAdded = now - listener.addedAt;
    const timeSinceLastCall = listener.lastCalled ? now - listener.lastCalled : timeSinceAdded;

    if (timeSinceAdded > STALE_THRESHOLD && timeSinceLastCall > STALE_THRESHOLD) {
      sharedMqttListeners.delete(id);
      removedCount++;
    }
  }

  if (removedCount > 0) {
    console.log(
      `[SharedMqtt] Cleaned up ${removedCount} stale listeners, remaining: ${sharedMqttListeners.size}`
    );
  }
};

export function SharedMqttProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  const [messageFlowHealthy, setMessageFlowHealthy] = useState(true);
  const clientRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const healthCheckIntervalRef = useRef(null);
  const visibilityChangeHandlerRef = useRef(null);
  const lastHealthyTimeRef = useRef(Date.now());

  // Refs to track current state for use in callbacks (avoids stale closures)
  const isConnectedRef = useRef(isConnected);
  const messageFlowHealthyRef = useRef(messageFlowHealthy);

  // Keep refs in sync with state
  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);

  useEffect(() => {
    messageFlowHealthyRef.current = messageFlowHealthy;
  }, [messageFlowHealthy]);

  // On mount, check if we already have an established connection from a previous render
  useEffect(() => {
    if (sharedMqttClient && sharedMqttClient.isConnected?.()) {
      setIsConnected(true);
      setMessageFlowHealthy(true);
      clientRef.current = sharedMqttClient;
    }
  }, []); // Only run on mount

  useEffect(() => {
    // Don't connect if not authenticated
    if (!isAuthenticated) {
      // If we have an existing connection, disconnect it
      if (sharedMqttClient) {
        console.log('[SharedMqtt] User not authenticated, disconnecting MQTT');
        sharedMqttClient.disconnect();
        sharedMqttClient = null;
        clientRef.current = null;
        setIsConnected(false);
      }
      // Cancel any pending message dispatch
      if (dispatchTimer) {
        clearTimeout(dispatchTimer);
        dispatchTimer = null;
        messageQueue = [];
      }
      // NOTE: We intentionally do NOT clear sharedMqttListeners here.
      // Listeners belong to providers (MqttActivityContext, MqttDebugContext)
      // that remain mounted across the logout/login cycle.  Clearing them
      // causes a silent disconnect: after re-login the MQTT WebSocket
      // delivers messages but no consumer is registered to process them.
      return;
    }

    // If we already have a shared client and it's connected, reuse it
    // Reset reconnectTrigger to 0 after successful reuse
    if (sharedMqttClient && sharedMqttClient.isConnected()) {
      clientRef.current = sharedMqttClient;
      setIsConnected(true);
      return;
    }

    // Create new shared MQTT connection

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const MQTT_WS_URL = `${wsProtocol}//${window.location.host}/ws/mqtt`;
    const MQTT_USERNAME = getConfigValue('mqtt.username');
    const MQTT_PASSWORD = getConfigValue('mqtt.password');
    const MQTT_TOPICS = ['#']; // Subscribe to all topics
    const MQTT_CLIENT_ID = generateSharedClientId(); // Generate unique client ID

    // Validate credentials - they MUST come from .env via docker-compose
    if (!MQTT_USERNAME || !MQTT_PASSWORD) {
      const errorMsg =
        'MQTT credentials not configured. Ensure MQTT_USERNAME and MQTT_PASSWORD are set in .env file.';
      console.error('[SharedMqtt] ' + errorMsg);
      setConnectionError(errorMsg);
      setIsConnected(false);
      return;
    }

    // Helper function to schedule reconnection (defined before use)
    const scheduleReconnection = (delay = 3000) => {
      // Clear any existing reconnection timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      // Don't reconnect if we're not authenticated
      if (!isAuthenticated) {
        return;
      }

      // Wait a bit before reconnecting to avoid rapid cycling
      reconnectTimeoutRef.current = setTimeout(() => {
        if (!isAuthenticated) return;

        // Always create fresh connection for reliability
        if (sharedMqttClient) {
          try {
            sharedMqttClient.disconnect();
          } catch (e) {
            // Ignore cleanup errors
          }
        }
        sharedMqttClient = null;
        clientRef.current = null;

        // Increment trigger to force new connection
        setReconnectTrigger((prev) => prev + 1);
      }, delay);
    };

    // IMPROVED: Unified health check that monitors both connection and message flow
    const setupHealthCheck = () => {
      // Clear any existing interval
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }

      lastHealthyTimeRef.current = Date.now();
      lastMessageTime = Date.now();

      // Health check every 10 seconds (more frequent for faster detection)
      healthCheckIntervalRef.current = setInterval(() => {
        const now = Date.now();
        const timeSinceLastMessage = now - lastMessageTime;
        const isClientConnected = sharedMqttClient?.isConnected?.() ?? false;

        // Log health status periodically
        if (listenerCallCount % 50 === 0) {
          console.log(
            `[SharedMqtt] Health check: connected=${isClientConnected}, listeners=${sharedMqttListeners.size}, timeSinceMsg=${Math.round(timeSinceLastMessage / 1000)}s`
          );
        }

        // Check for issues
        if (!isClientConnected) {
          console.warn('[SharedMqtt] Health check: Client not connected, scheduling reconnect');
          setIsConnected(false);
          setMessageFlowHealthy(false);
          scheduleReconnection(1000); // Quick reconnect for disconnection
          return;
        }

        // If connected but no messages for 5 minutes, something may be wrong.
        // Use a generous threshold — brief gaps are normal (few devices, backend restarts, etc.)
        if (timeSinceLastMessage > 300000 && sharedMqttListeners.size > 0) {
          console.warn(
            `[SharedMqtt] Health check: No messages for ${Math.round(timeSinceLastMessage / 1000)}s, forcing reconnection`
          );
          setMessageFlowHealthy(false);
          scheduleReconnection(500);
          return;
        }

        // Clean up stale listeners periodically
        cleanupStaleListeners();

        // Mark as healthy
        if (!messageFlowHealthy) {
          setMessageFlowHealthy(true);
        }
        lastHealthyTimeRef.current = now;
      }, 10000); // Check every 10 seconds
    };

    const client = new MqttWebSocket({
      url: MQTT_WS_URL,
      topics: MQTT_TOPICS,
      username: MQTT_USERNAME,
      password: MQTT_PASSWORD,
      clientId: MQTT_CLIENT_ID,
      onMessage: (msg) => {
        // Update last message time for health monitoring
        lastMessageTime = Date.now();

        // CRITICAL: If we're receiving messages, the connection IS working
        if (!isConnectedRef.current) {
          setIsConnected(true);
          setConnectionError(null);
        }
        if (!messageFlowHealthyRef.current) {
          setMessageFlowHealthy(true);
        }

        // Queue message for batched dispatch to listeners.
        // This lets the WebSocket onmessage handler return immediately,
        // preventing "[Violation] 'message' handler took Nms" warnings.
        messageQueue.push(msg);
        if (!dispatchTimer) {
          dispatchTimer = setTimeout(dispatchQueuedMessages, 16);
        }
      },
      onDisconnect: () => {
        setIsConnected(false);
        setMessageFlowHealthy(false);
        // Schedule reconnection
        scheduleReconnection(2000);
      },
      keepalive: 120, // 120s tolerates browser timer throttling when tab is backgrounded
      connectTimeout: 30000,
    });

    // Set up connection handlers
    client.onConnect = () => {
      setIsConnected(true);
      setConnectionError(null);
      setMessageFlowHealthy(true);
      lastMessageTime = Date.now();

      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Set up unified health check
      setupHealthCheck();
    };

    client.onError = (error) => {
      const msg = error?.message || 'Connection failed';
      const isKeepalive = msg.includes('Keepalive timeout');

      if (isKeepalive) {
        // Keepalive timeouts are routine when the tab was backgrounded.
        // Reconnect quickly and quietly without alarming error logs.
        console.log('[SharedMqtt] Keepalive timeout — reconnecting silently');
        setIsConnected(false);
        setMessageFlowHealthy(false);
        scheduleReconnection(500);
        return;
      }

      console.error('[SharedMqtt] Connection error:', error);
      setConnectionError(msg);
      setIsConnected(false);
      setMessageFlowHealthy(false);

      // Schedule reconnection attempt
      scheduleReconnection(5000); // Longer delay on real errors
    };

    // Store as singleton
    sharedMqttClient = client;
    clientRef.current = client;

    // Connect
    client.connect();

    // IMPROVED: Handle visibility changes (tab backgrounded/foregrounded)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab became visible - check if we need to reconnect
        const now = Date.now();
        const timeSinceLastHealthy = now - lastHealthyTimeRef.current;
        const isClientConnected = sharedMqttClient?.isConnected?.() ?? false;

        // If we've been backgrounded for more than 30 seconds and connection seems stale
        if (timeSinceLastHealthy > 30000 || !isClientConnected) {
          scheduleReconnection(100); // Quick reconnect on visibility change
        } else {
          // Just update the last healthy time
          lastHealthyTimeRef.current = now;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    visibilityChangeHandlerRef.current = handleVisibilityChange;

    // Cleanup on unmount (only if no other components are using it)
    return () => {
      // Clear intervals and timeouts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
        healthCheckIntervalRef.current = null;
      }
      if (dispatchTimer) {
        clearTimeout(dispatchTimer);
        dispatchTimer = null;
        messageQueue = [];
      }
      // Remove visibility change handler
      if (visibilityChangeHandlerRef.current) {
        document.removeEventListener('visibilitychange', visibilityChangeHandlerRef.current);
        visibilityChangeHandlerRef.current = null;
      }
      // Don't disconnect the shared client on unmount
      // It will be reused by other components
    };
  }, [isAuthenticated, reconnectTrigger]);

  // Listen for logout events to disconnect immediately
  useEffect(() => {
    const handleLogout = () => {
      if (sharedMqttClient) {
        sharedMqttClient.disconnect();
        sharedMqttClient = null;
        clientRef.current = null;
        setIsConnected(false);
        setConnectionError(null);
        setMessageFlowHealthy(false);
      }
      // Cancel any pending message dispatch
      if (dispatchTimer) {
        clearTimeout(dispatchTimer);
        dispatchTimer = null;
        messageQueue = [];
      }
      // Stop the health check + any pending reconnect immediately. The main
      // effect's cleanup also does this when isAuthenticated flips, but that
      // is a render later — without this, the interval fires one more
      // "not connected, scheduling reconnect" warn into the logged-out console.
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
        healthCheckIntervalRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    window.addEventListener('user-logout', handleLogout);
    window.addEventListener('session-timeout', handleLogout);

    return () => {
      window.removeEventListener('user-logout', handleLogout);
      window.removeEventListener('session-timeout', handleLogout);
    };
  }, []);

  // IMPROVED: Function to add a message listener with ID-based tracking
  // Returns a unique listener ID that should be used for removal
  // Options: { permanent: true } marks a listener as immune to stale cleanup
  const addMessageListener = useCallback((callback, debugName = 'anonymous', options = {}) => {
    if (typeof callback !== 'function') {
      console.warn('[SharedMqtt] Attempted to add non-function listener:', typeof callback);
      return null;
    }

    // Generate unique listener ID
    const listenerId = `listener_${++listenerIdCounter}_${Date.now()}`;

    // Store listener info with metadata for debugging and cleanup
    sharedMqttListeners.set(listenerId, {
      callback,
      debugName,
      addedAt: Date.now(),
      lastCalled: null,
      permanent: !!options.permanent,
    });

    return listenerId;
  }, []);

  // IMPROVED: Function to remove a message listener by ID
  // Also supports removing by callback reference for backward compatibility
  const removeMessageListener = useCallback((listenerIdOrCallback) => {
    if (typeof listenerIdOrCallback === 'string') {
      // Remove by ID (new method)
      const wasRemoved = sharedMqttListeners.delete(listenerIdOrCallback);
      return wasRemoved;
    } else if (typeof listenerIdOrCallback === 'function') {
      // Remove by callback reference (backward compatibility)
      // Search for the listener by callback
      for (const [id, info] of sharedMqttListeners.entries()) {
        if (info.callback === listenerIdOrCallback) {
          sharedMqttListeners.delete(id);
          return true;
        }
      }
      return false;
    }
    return false;
  }, []);

  // Function to get current listener count (for debugging)
  const getListenerCount = useCallback(() => {
    return sharedMqttListeners.size;
  }, []);

  // Function to get listener info (for debugging)
  const getListenerInfo = useCallback(() => {
    const info = [];
    for (const [id, listener] of sharedMqttListeners.entries()) {
      info.push({
        id,
        name: listener.debugName,
        age: Date.now() - listener.addedAt,
        lastCalled: listener.lastCalled ? Date.now() - listener.lastCalled : null,
      });
    }
    return info;
  }, []);

  // Function to publish a message - memoized to prevent unnecessary re-renders
  const publish = useCallback((topic, message) => {
    if (clientRef.current && clientRef.current.isConnected()) {
      return clientRef.current.publish(topic, message);
    }
    console.warn('[SharedMqtt] Cannot publish - not connected');
    return false;
  }, []);

  // Function to get connection status - memoized to prevent unnecessary re-renders
  const getConnectionStatus = useCallback(() => {
    if (!clientRef.current) return 'disconnected';
    if (clientRef.current.isConnected()) return 'connected';
    if (connectionError) return 'error';
    return 'connecting';
  }, [connectionError]);

  // IMPROVED: Function to force reconnection with optional listener cleanup
  const forceReconnect = useCallback((options = {}) => {
    const { clearStaleListeners = true, delay = 0 } = options;

    // Optionally clean up stale listeners before reconnecting
    if (clearStaleListeners) {
      cleanupStaleListeners();
    }

    // Clear any pending reconnection
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Clear health check interval
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current);
      healthCheckIntervalRef.current = null;
    }

    // Disconnect existing client if it exists
    if (sharedMqttClient) {
      try {
        sharedMqttClient.disconnect();
      } catch (err) {
        console.error('[SharedMqtt] Error disconnecting during force reconnect:', err);
      }
    }

    // Clear the singleton to force creation of a new connection
    sharedMqttClient = null;
    clientRef.current = null;
    setIsConnected(false);
    setMessageFlowHealthy(false);

    // Trigger reconnection by incrementing reconnectTrigger
    // This will cause the useEffect to run and create a new connection
    if (delay > 0) {
      setTimeout(() => {
        setReconnectTrigger((prev) => prev + 1);
      }, delay);
    } else {
      setReconnectTrigger((prev) => prev + 1);
    }
  }, []);

  const value = useMemo(
    () => ({
      isConnected,
      connectionError,
      messageFlowHealthy,
      addMessageListener,
      removeMessageListener,
      publish,
      getConnectionStatus,
      forceReconnect,
      getListenerCount,
      getListenerInfo,
      client: clientRef.current,
    }),
    [
      isConnected,
      connectionError,
      messageFlowHealthy,
      addMessageListener,
      removeMessageListener,
      publish,
      getConnectionStatus,
      forceReconnect,
      getListenerCount,
      getListenerInfo,
    ]
  );

  return <SharedMqttContext.Provider value={value}>{children}</SharedMqttContext.Provider>;
}
